import { Title } from '@solidjs/meta'
import type { Component } from 'solid-js'
import { createSignal } from 'solid-js'
import Button from '~/components/Button'
import Card from '~/components/Card'
import Container from '~/components/Container'
import Icon from '~/components/Icon'

const ButtonGroupPage: Component = () => {
  const [selectedView, setSelectedView] = createSignal<'list' | 'grid' | 'table'>('list')

  return (
    <>
      <Title>Button Group - Florez Jaramillo Solutions</Title>

      <Container class="py-8">
        <div class="mb-10 mt-6 text-balance">
          <h1 class="text-4xl font-bold text-foreground mb-4">Button Group</h1>
          <p class="text-foreground-subtle text-xl max-w-3xl leading-relaxed">
            Agrupación lógica de acciones relacionadas. Button Group permite presentar botones en una secuencia continua, ideal para selectores de estado, barras de herramientas y controles de
            paginación.
          </p>
        </div>

        <div class="space-y-12">
          {/* Variantes y Direccionamiento */}
          <Card>
            <Card.Header
              title="Tipos de Agrupación"
              subtitle="Controla la orientación y el estilo visual de los bordes compartidos."
              icon={<Icon icon="material-symbols-light:menu" class="text-primary-500" size={24} />}
            />
            <Card.Body>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-10 p-8 bg-surface-muted rounded-xl border border-border">
                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-primary-500">Orientación Horizontal</h4>
                  <div class="flex flex-wrap gap-4">
                    <Button.Group variant="contained" color="primary">
                      <Button>Inicio</Button>
                      <Button>Medio</Button>
                      <Button>Fin</Button>
                    </Button.Group>

                    <Button.Group variant="outlined" color="secondary">
                      <Button icon="material-symbols-light:home-outline" />
                      <Button icon="material-symbols-light:settings-outline" />
                      <Button icon="material-symbols-light:person-outline" />
                    </Button.Group>
                  </div>
                </div>

                <div class="space-y-6">
                  <h4 class="text-xs font-bold text-foreground-subtle uppercase tracking-widest pl-3 border-l-4 border-secondary-500">Orientación Vertical</h4>
                  <div class="flex gap-10">
                    <Button.Group vertical color="info">
                      <Button>Top</Button>
                      <Button>Mid</Button>
                      <Button>Bottom</Button>
                    </Button.Group>

                    <Button.Group vertical variant="outlined" size="sm">
                      <Button icon="material-symbols-light:add-circle-outline" />
                      <Button icon="material-symbols-light:do-not-disturb-on" />
                    </Button.Group>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Variantes de Estilo */}
          <Card>
            <Card.Header
              title="Estética y Personalización"
              subtitle="Combina colores, tamaños y variantes de botón dentro de un mismo grupo."
              icon={<Icon icon="material-symbols-light:palette-outline" class="text-secondary-500" size={24} />}
            />
            <Card.Body>
              <div class="space-y-10">
                <div class="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div class="p-6 bg-surface rounded-xl border-t-4 border-primary-500 shadow-sm space-y-4">
                    <p class="text-xs font-bold text-foreground-subtle">SOFT VARIANT</p>
                    <Button.Group variant="soft" color="primary" fullWidth>
                      <Button>Q1</Button>
                      <Button>Q2</Button>
                      <Button>Q3</Button>
                    </Button.Group>
                  </div>

                  <div class="p-6 bg-surface rounded-xl border-t-4 border-success-500 shadow-sm space-y-4">
                    <p class="text-xs font-bold text-foreground-subtle">TEXT VARIANT</p>
                    <Button.Group variant="text" color="success" size="sm">
                      <Button>View</Button>
                      <Button>Edit</Button>
                      <Button>Delete</Button>
                    </Button.Group>
                  </div>

                  <div class="p-6 bg-surface rounded-xl border-t-4 border-warning-500 shadow-sm space-y-4">
                    <p class="text-xs font-bold text-foreground-subtle">MIXED SIZES</p>
                    <Button.Group size="xl" color="warning">
                      <Button icon="material-symbols-light:play-arrow-outline" />
                      <Button icon="material-symbols-light:pause-outline" />
                      <Button icon="material-symbols-light:stop-outline" />
                    </Button.Group>
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Ejemplo Real: Selector de Vista */}
          <Card>
            <Card.Header
              title="Caso Práctico: Alternador de Estado"
              subtitle="Utiliza el grupo de botones como un selector de modo exclusivo tipo 'Segmented Control'."
              icon={<Icon icon="material-symbols-light:widgets-outline" class="text-amber-500" size={24} />}
            />
            <Card.Body>
              <div class="p-10 flex flex-col items-center bg-surface-muted rounded-2xl border border-border">
                <Button.Group color="primary" variant="outlined" class="bg-surface p-1 rounded-xl shadow-lg border-2">
                  <Button onClick={() => setSelectedView('list')} variant={selectedView() === 'list' ? 'contained' : 'text'} startIcon="material-symbols-light:list" class="rounded-lg px-6">
                    Lista
                  </Button>
                  <Button
                    onClick={() => setSelectedView('grid')}
                    variant={selectedView() === 'grid' ? 'contained' : 'text'}
                    startIcon="material-symbols-light:grid-view-outline"
                    class="rounded-lg px-6"
                  >
                    Cuadrícula
                  </Button>
                  <Button onClick={() => setSelectedView('table')} variant={selectedView() === 'table' ? 'contained' : 'text'} startIcon="material-symbols-light:checklist" class="rounded-lg px-6">
                    Tabla
                  </Button>
                </Button.Group>

                <div class="mt-8 p-6 w-full max-w-md bg-surface rounded-xl border border-border text-center animate-in fade-in slide-in-from-bottom-2 duration-500">
                  <p class="text-foreground-subtle text-sm mb-1 uppercase tracking-tighter font-bold">Modo de visualización</p>
                  <h3 class="text-2xl font-black text-primary-600 capitalize italic">{selectedView()}</h3>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* API Reference */}
          <Card>
            <Card.Header title="API Reference" subtitle="Manual técnico para Button.Group." icon={<Icon icon="material-symbols-light:code" class="text-foreground-subtle" size={24} />} />
            <Card.Body>
              <div class="overflow-x-auto rounded-xl border border-border">
                <table class="w-full text-sm text-left">
                  <thead class="text-xs uppercase bg-surface-muted text-foreground-subtle">
                    <tr>
                      <th class="px-6 py-4 border-b">Propiedad</th>
                      <th class="px-6 py-4 border-b">Tipo</th>
                      <th class="px-6 py-4 border-b">Default</th>
                      <th class="px-6 py-4 border-b">Descripción</th>
                    </tr>
                  </thead>
                  <tbody class="divide-y divide-border">
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">variant</td>
                      <td class="px-6 py-4">ButtonVariantType</td>
                      <td class="px-6 py-4 font-mono">'contained'</td>
                      <td class="px-6 py-4">Estilo visual compartido por todos los botones del grupo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">color</td>
                      <td class="px-6 py-4">MainColorsType</td>
                      <td class="px-6 py-4 font-mono">'primary'</td>
                      <td class="px-6 py-4">Paleta de color aplicada en bloque al grupo.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">size</td>
                      <td class="px-6 py-4 italic">MainSizesType</td>
                      <td class="px-6 py-4 font-mono">'md'</td>
                      <td class="px-6 py-4 italic text-balance">Dimensiones uniformes (xs, sm, md, lg, xl).</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold text-primary-600">vertical</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4 text-balance">Apila los botones verticalmente con bordes redondeados en extremos.</td>
                    </tr>
                    <tr>
                      <td class="px-6 py-4 font-mono font-bold">fullWidth</td>
                      <td class="px-6 py-4">boolean</td>
                      <td class="px-6 py-4 font-mono">false</td>
                      <td class="px-6 py-4">Expande el grupo para ocupar el 100% del ancho del padre.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </Card.Body>
          </Card>
        </div>
      </Container>
    </>
  )
}

export default ButtonGroupPage
